import React, { useState, useEffect } from 'react';
import { Building, Flag, Globe, Factory as FactoryIcon, University, Newspaper } from 'lucide-react';
import { Button } from './ui/button';
import { useSettings } from '../contexts/SettingsContext';
import { getPartners } from '../api/client';
import { logoImage } from '../utils/imageOptimizer';

const defaultIcons = [Building, Flag, Globe, FactoryIcon, University, Newspaper];

export const PartnersSection = () => {
  const { settings } = useSettings();
  const [activeFilter, setActiveFilter] = useState('All');
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const filters = ['All', 'Government', 'Associations', 'Media', 'Corporate'];

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const data = await getPartners();
      setPartners(data.partners || []);
    } catch {
      // Partners will show placeholders
    } finally {
      setLoading(false);
    }
  };

  const filteredPartners = activeFilter === 'All' 
    ? partners 
    : partners.filter(p => p.category === activeFilter);

  // Show placeholder if no partners
  const showPlaceholders = partners.length === 0;

  return (
    <section id="partners" className="bg-gray-50 py-20">
      <div className="container mx-auto px-6">
        <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">
          {settings.partners?.title || 'Our Partners'}
        </h2>
        <div className="flex flex-wrap justify-center gap-4 mb-10">
          {filters.map((filter) => (
            <Button
              key={filter}
              variant="outline"
              size="sm"
              onClick={() => setActiveFilter(filter)}
              className={`${
                activeFilter === filter
                  ? 'text-white border-transparent hover:text-white'
                  : 'bg-white border-gray-300 hover:bg-gray-100'
              }`}
              style={activeFilter === filter ? { backgroundColor: settings.colors?.primary || '#0E7A5F' } : {}}
            >
              {filter}
            </Button>
          ))}
        </div>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-green-600 border-t-transparent rounded-full"></div>
          </div>
        ) : showPlaceholders ? (
          // Show placeholder icons if no partners
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
            {defaultIcons.map((Icon, idx) => (
              <div
                key={`placeholder-${idx}`}
                className="bg-gradient-to-br from-gray-100 to-gray-200 h-32 rounded-xl flex items-center justify-center hover:shadow-md transition-shadow"
              >
                <div className="text-gray-500 text-center">
                  <Icon size={32} className="mx-auto mb-2" />
                  <div className="text-sm">Partner Logo</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Show actual partners
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
            {filteredPartners.map((partner) => (
              <a
                key={partner.id}
                href={partner.url || '#'}
                target={partner.url ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className="bg-white h-32 rounded-xl flex items-center justify-center hover:shadow-lg transition-shadow border border-gray-100 p-4"
              >
                {partner.logo ? (
                  <img 
                    src={logoImage(partner.logo)} 
                    alt={partner.name} 
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <div className="text-gray-500 text-center">
                    <Building size={32} className="mx-auto mb-2" />
                    <div className="text-sm font-medium">{partner.name}</div>
                  </div>
                )}
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
